import { apiGet, apiPost } from '../api/client'
import type { Order, TableState } from './DataTable'

/**
 * Asezarile listelor, pe om, IN BAZA - `OdTableState` din `public/js/od_ux.js`
 * si `General::tableStates` / `saveTableState` din portal.
 *
 * DE CE NU AJUNGE `localStorage`
 * -----------------------------------------------------------------------------
 * Pana acum asezarea statea numai in browser: se pierdea la alt calculator, la
 * alt browser, la o golire de cache. Asezarea unei liste e munca omului, deci
 * se scrie langa el, in baza (`it_user_table_state`), si il urmeaza oriunde
 * s-ar conecta. `localStorage` ramane oglinda rapida: se citeste pe loc, ca
 * lista sa nu tresara la incarcare, si se corecteaza din baza cand raspunde ea.
 *
 * RASPUNDE PE LOC, NICIODATA MAI TARZIU
 * -----------------------------------------------------------------------------
 * Ca in `od_ux.js`: toate asezarile omului se cer O DATA pe pagina (`prime`),
 * prima lista porneste cererea si celelalte asteapta aceeasi promisiune; lista
 * se ridica cu ce e in browser si ia asezarea din baza cand vine. Scrierea se
 * face o singura data, la o secunda dupa ce omul s-a oprit - o tragere de
 * coloana emite starea de zece ori.
 *
 * ACEEASI CHEIE SI ACEEASI FORMA CA IN PORTAL: `table_key` e id-ul tabelului
 * (`admWindowsTable`), iar starea se scrie in forma DataTables (`start`,
 * `length`, `order: [[col, dir]]`, `columns[].visible`), plus `hidden`. Asa ce
 * a salvat omul in portal se citeste aici, si invers.
 *
 * CE NU SE SCRIE: cautarea. O cautare ramasa din alta zi deschide o lista din
 * care lipsesc randuri, si primul gand nu e "am filtrat eu", ci "s-au pierdut
 * datele".
 */

const SAVE_DELAY_MS = 1000

/** Forma DataTables a unei asezari, cea din `it_user_table_state`. */
export interface StoredState {
  start?: number
  length?: number
  order?: Array<[number, string]>
  columns?: Array<{ visible?: boolean }>
  /** Al lui v4: indicii coloanelor ascunse din meniul "Columns". */
  hidden?: number[]
  /**
   * Ordinea coloanelor, pe indice - EXACT cum o scrie ColReorder-ul din
   * DataTables in stateSave (`state.ColReorder`), ca portalul, daca ar citi
   * starea, s-o inteleaga. Lipsa = ordinea din model.
   */
  ColReorder?: number[]
  time?: number
}

let cache: Record<string, StoredState> | null = null
let pending: Promise<Record<string, StoredState>> | null = null
const timers: Record<string, ReturnType<typeof setTimeout>> = {}
const listeners = new Set<() => void>()

/** Toate asezarile omului, o singura data pe pagina. */
export function primeTableStates(): Promise<Record<string, StoredState>> {
  if (cache) return Promise.resolve(cache)
  if (!pending) {
    pending = apiGet<{ status: string; states: Record<string, StoredState> }>('/general/table-states')
      .then((res) => {
        cache = res && res.states && typeof res.states === 'object' ? res.states : {}
        listeners.forEach((fn) => fn())
        return cache
      })
      .catch(() => {
        /* Baza nu raspunde: nu e o pana a listei. Se merge cu ce e in browser. */
        cache = {}
        return cache
      })
  }
  return pending
}

/** Asezarea din baza, daca a ajuns deja. */
export function storedState(key: string): StoredState | null {
  return cache && cache[key] ? cache[key] : null
}

/** Cine vrea sa afle cand vin asezarile din baza. Intoarce dez-abonarea. */
export function onTableStates(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Scrie asezarea in baza, o data, la o secunda dupa ultima schimbare. */
export function saveTableState(key: string, state: StoredState): void {
  if (cache) cache[key] = state
  if (timers[key]) clearTimeout(timers[key])
  timers[key] = setTimeout(() => {
    delete timers[key]
    apiPost('/general/table-state', { key, state: JSON.stringify(state) }).catch(() => {
      /* Ramane copia din browser; se incearca la urmatoarea schimbare. */
    })
  }, SAVE_DELAY_MS)
}

/** Forma DataTables -> ce tine `useTableState`. Cautarea si `start` nu se iau. */
export function fromStored(stored: StoredState, initial: TableState): Partial<TableState> {
  const out: Partial<TableState> = {}
  if (typeof stored.length === 'number' && stored.length > 0) out.length = stored.length
  const first = Array.isArray(stored.order) ? stored.order[0] : undefined
  if (Array.isArray(first) && typeof first[0] === 'number') {
    out.order = { column: first[0], dir: first[1] === 'desc' ? 'desc' : 'asc' } as Order
  }
  if (Array.isArray(stored.hidden)) {
    out.hidden = stored.hidden.filter((n): n is number => typeof n === 'number')
  } else if (Array.isArray(stored.columns)) {
    /* Starea scrisa de DataTables in portal: coloanele ascunse sunt cele cu
       `visible: false`. */
    const hidden: number[] = []
    stored.columns.forEach((c, i) => {
      if (c && c.visible === false) hidden.push(i)
    })
    if (hidden.length > 0) out.hidden = hidden
  }
  if (Array.isArray(stored.ColReorder)) {
    const sequence = stored.ColReorder.filter((n): n is number => typeof n === 'number')
    if (sequence.length > 0) out.sequence = sequence
  }
  void initial
  return out
}

/** Ce tine `useTableState` -> forma DataTables, fara cautare. */
export function toStored(state: TableState): StoredState {
  return {
    time: Date.now(),
    start: 0,
    length: state.length,
    order: [[state.order.column, state.order.dir]],
    hidden: state.hidden ?? [],
    ...(state.sequence && state.sequence.length > 0 ? { ColReorder: state.sequence } : {}),
  }
}

/** Numai pentru probe: uita ce s-a citit din baza. */
export function resetTableStates(): void {
  cache = null
  pending = null
  for (const key of Object.keys(timers)) {
    clearTimeout(timers[key])
    delete timers[key]
  }
  listeners.clear()
}
